/**
 * Die Aktionsfarben eines Heimatunternehmens — rechnerisch aus `company.colorHex`
 * abgeleitet, als REINE Funktion ohne Datenbank und ohne DOM.
 *
 * Warum `ui` und nicht `domain` (Ruling UV-A): Angewandt wird die Ableitung
 * ausschliesslich in der Oberfläche — sie setzt die Akzent-Tokens beim Sitzungsaufbau
 * (theme/aktionsfarben.ts) und zeigt die Vorschau im Anlege-Dialog. SERVERSEITIG nutzt
 * sie niemand: Die API liefert nur den Ausgangswert `colorHex`. `web` darf laut
 * Randbedingung (scripts/check-boundaries.mjs) aber nur `ui` und `api-client`
 * importieren, `domain` also nicht — in `domain` wäre sie für ihren einzigen Aufrufer
 * unerreichbar gewesen. Sie ist deshalb hierher UMGEZOGEN, nicht gespiegelt: Es gibt
 * genau EINE Rechnung, eine zweite Fassung liefe irgendwann auseinander.
 *
 * Der Kern ist eine RECHNUNG, keine Farbtabelle (Dirks Entscheid: weitere Unternehmen
 * müssen grundsätzlich anlegbar sein, die Marke steckt in den Stammdaten). Gerechnet wird
 * nach WCAG 2.x: relative Luminanz aus den linearisierten sRGB-Kanälen, daraus das
 * Kontrastverhältnis, und daraus schrittweise der Ton, auf dem weißer Text die Schwelle
 * 4,5:1 erreicht.
 */
/** Die AA-Schwelle für normalen Text (WCAG 2.x, 1.4.3). Alles, was hier abgeleitet wird,
 *  trägt Text — Knöpfe, Links, aktive Zustände. */
export const KONTRAST_MINDESTWERT = 4.5;
/**
 * Der dunkle Grund des Dunkelmodus (ME-Farbschema). Gegen ihn wird die aufgehellte
 * Fassung gemessen: Ein Ton, der auf Weiß gut liegt, ist auf diesem Grund oft zu dunkel.
 */
export const DUNKLER_GRUND = "#161617";
/** Weiß — der Text, der auf dem Aktionston stehen soll. */
const WEISS = "#FFFFFF";
/**
 * Die Schrittweite einer „Stufe": 8 % je Schritt.
 *
 * Sie tut zwei Dinge zugleich, und deshalb ist ihre Größe ein Kompromiss. Die
 * Kontrastsuche geht in genau diesen Stufen (kleiner wäre genauer, aber der gefundene
 * Ton läge dichter an der Schwelle — 8 % überschreiten sie um einen kleinen Sicherheits-
 * abstand). Und Hover/Gedrückt sind je EINE solche Stufe: 8 % sind sichtbar, ohne dass
 * ein Knopf beim Überfahren die Farbe zu wechseln scheint. Bei 4 % wäre der Hover kaum
 * wahrnehmbar, bei 15 % wirkte er wie ein anderer Knopf.
 */
const STUFE = 0.08;
/**
 * Eine harte Obergrenze für beide Suchen. Sie ist nicht die Abbruchbedingung — die sind
 * Schwarz (nicht weiter abdunkelbar, 21:1 gegen Weiß) und Weiß (nicht weiter aufhellbar,
 * rund 18:1 gegen #161617), und beide werden nachweislich erreicht. Sie ist der Riegel
 * gegen einen künftigen Programmfehler, der aus der Schleife eine Endlosschleife machte;
 * eine Anmeldung darf daran nicht hängen bleiben.
 */
const HOECHSTZAHL_STUFEN = 200;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
/**
 * Ist das eine Farbe, mit der hier gerechnet werden kann — `#rrggbb`, gross oder klein?
 *
 * Die EINE Stelle, an der das entschieden wird. Sie ist ausdrücklich exportiert, damit
 * niemand daneben eine zweite Vorstellung davon aufbaut, was eine Farbe ist: Wer wissen
 * will, ob ein Wert taugt, BEVOR er eine werfende Funktion ruft, fragt hier. Kurzformen
 * (`#abc`), Farbnamen und `rgb(…)` gehören nicht dazu — die Spalte `company.colorHex` ist
 * per `ck_company_color` auf genau diese Form festgelegt.
 */
export function istFarbwert(wert) {
    return typeof wert === "string" && HEX_RE.test(wert);
}
/**
 * Zerlegt `#rrggbb` in drei Kanäle 0..255.
 *
 * Wirft bei allem anderen. Die Spalte `company.colorHex` ist per `ck_company_color` auf
 * genau diese Form festgelegt und `validateCompanyNeu` prüft sie am Eingang — kommt hier
 * trotzdem etwas anderes an, ist eine dieser beiden Zusagen gebrochen. Das soll laut
 * sein: Ein stiller Rückfall auf Schwarz erzeugte eine Oberfläche, die schlicht falsch
 * aussieht, ohne dass jemand erführe, warum.
 */
function kanaele(farbe) {
    if (!istFarbwert(farbe)) {
        throw new Error(`Keine gültige Farbe: ${String(farbe)} (erwartet wird #rrggbb).`);
    }
    return [
        Number.parseInt(farbe.slice(1, 3), 16),
        Number.parseInt(farbe.slice(3, 5), 16),
        Number.parseInt(farbe.slice(5, 7), 16),
    ];
}
/** Zurück in die Großform #RRGGBB — dieselbe Schreibweise wie der geseedete Bestand. */
function alsHex(rgb) {
    return `#${rgb.map((k) => k.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}
/**
 * Ein Kanal von der sRGB-Darstellung in den LINEAREN Lichtwert (WCAG 2.x, Definition der
 * relativen Luminanz). Der Knick bei 0,04045 ist Teil der sRGB-Übertragungsfunktion; ohne
 * die Linearisierung wären alle Kontrastwerte im dunklen Bereich deutlich zu günstig.
 */
function linear(kanal) {
    const s = kanal / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}
/**
 * Die relative Luminanz nach WCAG 2.x: 0 für Schwarz, 1 für Weiß. Grün wiegt am
 * schwersten (0,7152), Blau am leichtesten (0,0722) — das ist keine Willkür, sondern die
 * Empfindlichkeit des Auges.
 */
export function relativeLuminanz(farbe) {
    const [r, g, b] = kanaele(farbe);
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}
/**
 * Das Kontrastverhältnis zweier Farben nach WCAG 2.x: (heller + 0,05) / (dunkler + 0,05),
 * zwischen 1:1 (gleiche Farbe) und 21:1 (Schwarz auf Weiß). Richtungsunabhängig — welche
 * der beiden der Text ist und welche der Grund, spielt für die Zahl keine Rolle.
 */
export function kontrastVerhaeltnis(a, b) {
    const la = relativeLuminanz(a);
    const lb = relativeLuminanz(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/**
 * Eine Stufe dunkler: jeder Kanal auf (1 − STUFE) seines Werts, abgerundet.
 *
 * Multiplikativ, nicht subtraktiv — das hält das Verhältnis der Kanäle zueinander und
 * damit den Farbton weitgehend fest; ein fester Abzug ließe gesättigte Farben in Richtung
 * Grau kippen. Abgerundet wird, damit jeder Schritt bei einem Kanal > 0 auch wirklich
 * kleiner wird und die Suche in endlich vielen Schritten bei Schwarz ankommt.
 */
function eineStufeDunkler(rgb) {
    const [r, g, b] = rgb;
    const ab = (k) => Math.floor(k * (1 - STUFE));
    return [ab(r), ab(g), ab(b)];
}
/**
 * Eine Stufe heller: jeder Kanal um STUFE des noch verbleibenden Wegs nach Weiß nach
 * oben, aufgerundet und mindestens um 1.
 *
 * Der „verbleibende Weg" statt eines festen Faktors, weil ein Kanal auf 0 sonst für immer
 * auf 0 bliebe (0 × irgendetwas ist 0) — Schwarz ließe sich nicht aufhellen. Das
 * Mindest-1 sorgt dafür, dass auch kurz vor Weiß noch Fortschritt entsteht und die Suche
 * endet.
 */
function eineStufeHeller(rgb) {
    const [r, g, b] = rgb;
    const auf = (k) => Math.min(255, k + Math.max(1, Math.ceil((255 - k) * STUFE)));
    return [auf(r), auf(g), auf(b)];
}
/**
 * Sucht schrittweise den ersten Ton, der gegen `bezug` mindestens KONTRAST_MINDESTWERT
 * erreicht. Erreicht die Eingabe die Schwelle schon, kommt sie UNVERÄNDERT zurück — eine
 * Farbe, auf der weißer Text ohnehin steht, wird nicht künstlich dunkler gemacht.
 */
function bisKontrast(start, bezug, schritt) {
    let aktuell = start;
    for (let i = 0; i < HOECHSTZAHL_STUFEN; i++) {
        if (kontrastVerhaeltnis(alsHex(aktuell), bezug) >= KONTRAST_MINDESTWERT)
            break;
        const naechster = schritt(aktuell);
        // Kein Fortschritt mehr (Schwarz bzw. Weiß erreicht): weiter zu gehen brächte nichts.
        if (alsHex(naechster) === alsHex(aktuell))
            break;
        aktuell = naechster;
    }
    return aktuell;
}
/**
 * Leitet die vier Aktionstöne aus der Unternehmensfarbe ab.
 *
 * - **aktion**: `colorHex`, so lange stufenweise abgedunkelt, bis WEISSER Text darauf
 *   4,5:1 erreicht. Die Marke selbst ist dafür oft zu hell (ME-Grün #7AB41E liegt bei
 *   2,5:1) — genau deshalb gibt es im ME-Farbschema neben der Marke einen eigenen
 *   Aktionston. Ist die Marke dunkel genug, bleibt sie unverändert.
 * - **hover** / **gedrueckt**: je eine weitere Stufe dunkler. Abdunkeln kann den Kontrast
 *   zu Weiß nur erhöhen, die Lesbarkeitszusage gilt für beide mit.
 * - **hellFuerDunkelmodus**: `colorHex`, so lange stufenweise aufgehellt, bis sie gegen
 *   den dunklen Grund `#161617` 4,5:1 erreicht. Dieselbe Regel, andere Richtung — nicht
 *   der Aktionston aufgehellt, sondern die MARKE: Der abgedunkelte Ton wäre der Umweg
 *   über eine Farbe, die im Dunkelmodus gar nicht vorkommt.
 *
 * Rein und deterministisch: gleiche Eingabe, gleiche Ausgabe, kein Zustand, keine Zeit.
 */
export function aktionsfarbenAus(colorHex) {
    const start = kanaele(colorHex);
    const aktion = bisKontrast(start, WEISS, eineStufeDunkler);
    const hover = eineStufeDunkler(aktion);
    const gedrueckt = eineStufeDunkler(hover);
    const hell = bisKontrast(start, DUNKLER_GRUND, eineStufeHeller);
    return {
        aktion: alsHex(aktion),
        hover: alsHex(hover),
        gedrueckt: alsHex(gedrueckt),
        hellFuerDunkelmodus: alsHex(hell),
    };
}
//# sourceMappingURL=farben.js.map